import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { TraegerStatusCard } from "@/components/TraegerStatusCard";
import { toast } from "sonner";
import { ArrowLeft, UserSquare2, GraduationCap, CalendarCheck, ClipboardSignature, MessageSquare, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WbAkteStammdaten from "@/components/WbAkteStammdaten";
import { WbAkteMassnahme, WbAkteAnwesenheit, WbAkteNotizen, openPdf } from "@/components/WbAkteSections";
import TraegerFormPanel from "@/components/TraegerFormPanel";
import ExamResultCard from "@/components/ExamResultCard";
import ComplaintFormButton from "@/components/ComplaintFormButton";
import QuestionnaireCard from "@/components/QuestionnaireCard";
import WbDatevPanel from "@/components/WbDatevPanel";
import CvProfileReadOnly from "@/components/CvProfileReadOnly";
import { WbClassLinks, WbCalendar, WbJobs, WbReport } from "@/components/WbAkteExtras";
import { CalendarDays, Briefcase, UserCircle, FileText as FileTextIcon, FileCheck2 } from "lucide-react";

const TABS = [
  ["stammdaten", "Übersicht", UserSquare2], ["massnahme", "Maßnahme & Module", GraduationCap], ["anwesenheit", "Anwesenheit", CalendarCheck],
  ["kalender", "Kalender", CalendarDays], ["bewerbung", "Bewerbung", Briefcase], ["lebenslauf", "Lebenslauf", UserCircle],
  ["zwischenbericht", "Zwischenbericht", FileTextIcon], ["abschlussbericht", "Abschlussbericht", FileCheck2],
  ["unterlagen", "Teilnehmerunterlagen", ClipboardSignature], ["notizen", "Notizen", MessageSquare],
];

export default function WeiterbildungKundeAktePage() {
  const { participantId } = useParams();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(["traeger", "vertrag"].includes(sp.get("tab")) ? "unterlagen" : sp.get("tab") || "stammdaten");
  const roles = user?.all_roles?.length ? user.all_roles : [user?.role];
  const canEdit = roles.some((r) => r === "admin" || r === "manager");

  const load = useCallback(() => api.get(`/weiterbildung/participants/${participantId}`).then((r) => setDetail(r.data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message)), [participantId]);
  useEffect(() => { load(); }, [load]);

  if (!detail) return <div className="p-8 text-xs text-slate-400">Lädt…</div>;
  const p = detail.participant;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-6" data-testid="wb-akte-page">
      <Link to="/weiterbildung" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="wb-akte-back"><ArrowLeft size={12} /> Weiterbildung</Link>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Kundenakte · Weiterbildung</div>
        <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900" data-testid="wb-akte-name">{p.name}</h1>
        <div className="text-sm text-slate-500 mt-1">{p.email}{p.customer_number ? ` · Kd.-Nr. ${p.customer_number}` : ""}{p.kostentraeger ? ` · ${p.kostentraeger}` : ""}</div>
      </div>
      <WbClassLinks classId={p.class_id} />
      <ExamResultCard participant={p} />
      <QuestionnaireCard personKind="wb" personId={p.participant_id} />
      <ComplaintFormButton kind="wb" personId={p.participant_id} />
      {canEdit && <TraegerStatusCard kind="participant" personId={p.participant_id} onlyMissing />}
      <div className="flex gap-1 border-b border-slate-200 flex-wrap" data-testid="wb-akte-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`wb-akte-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon size={14} strokeWidth={1.5} /> {l}
          </button>
        ))}
      </div>
      {tab === "stammdaten" && <WbAkteStammdaten participant={p} canEdit={canEdit} onSaved={load} />}
      {tab === "massnahme" && <WbAkteMassnahme detail={detail} />}
      {tab === "anwesenheit" && <WbAkteAnwesenheit detail={detail} />}
      {tab === "unterlagen" && (
        <div className="border border-slate-200 bg-white p-6 space-y-3" data-testid="wb-akte-vertrag">
          <p className="text-sm text-slate-600">Teilnahmevertrag mit den gebuchten Modulen, UE und Preisen aus dem Katalog.</p>
          <button onClick={() => openPdf(`/weiterbildung/participants/${p.participant_id}/contract.pdf`, `Teilnahmevertrag_${p.name}.pdf`)} className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 text-sm hover:bg-slate-700" data-testid="wb-akte-contract-btn">
            <Download size={14} /> Teilnahmevertrag (PDF)
          </button>
        </div>
      )}
      {tab === "kalender" && <WbCalendar cls={detail.class} />}
      {tab === "bewerbung" && <WbJobs pid={p.participant_id} />}
      {tab === "lebenslauf" && <CvProfileReadOnly url={`/wb-akte/${p.participant_id}/cv-profile`} />}
      {tab === "zwischenbericht" && <WbReport pid={p.participant_id} kind="interim" label="Zwischenbericht" />}
      {tab === "abschlussbericht" && <WbReport pid={p.participant_id} kind="final" label="Abschlussbericht" />}
      {tab === "unterlagen" && (
        <div className="space-y-2" data-testid="wb-akte-unterlagen">
          <WbDatevPanel participantId={p.participant_id} />
          <TraegerFormPanel kind="participant" personId={p.participant_id} />
        </div>
      )}
      {tab === "notizen" && <WbAkteNotizen participantId={p.participant_id} />}
    </div>
  );
}
